import { useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'

import type { Me } from '../lib/api'
import { THEME_LABELS, THEMES, useTheme } from '../lib/theme'

/**
 * The account card at the foot of the sidebar, and its menu.
 *
 * **The menu is positioned, not inserted.** The first version rendered the
 * items as a sibling block below the trigger inside an `mt-auto` container, so
 * opening it *pushed the card upwards* — the thing you just clicked moved out
 * from under the pointer. That is the classic layout-shift bug, and it is worth
 * naming why it matters beyond looking untidy: a control that moves on click
 * invites a mis-click on whatever slides into its place, and here the
 * neighbouring item is **Sign out**.
 *
 * So the patterns this follows, all of which the broken version lacked:
 *
 * | Pattern | Why |
 * |---|---|
 * | **Absolute, anchored above the trigger** | Takes the menu out of flow, so nothing reflows. It opens upward because the trigger sits at the bottom of the viewport |
 * | **`role="menu"` + `menuitem`, `aria-haspopup`, `aria-expanded`** | A screen reader is told it is a menu, whether it is open, and how many items |
 * | **Escape closes and returns focus to the trigger** | Otherwise focus is stranded on a hidden element |
 * | **Click or focus outside closes it** | A menu that only closes by clicking the same button is a trap on touch |
 * | **A chevron that reflects state** | The trigger says it is a disclosure rather than a label |
 * | **`title` on a truncated address** | 5–10 client users, and an email is how they are identified |
 *
 * Deliberately *not* a library: `DESIGN-SYSTEM.md` rule 2 — accessible
 * primitives are hand-built against the tokens, because the client owns the IP.
 */
export function UserMenu({
  me,
  onSignOut,
  collapsed = false,
}: {
  me: Me | null
  onSignOut: () => void
  collapsed?: boolean
}) {
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const { theme, setTheme } = useTheme()

  useEffect(() => {
    if (!open) return

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        trigger.current?.focus()
      }
    }
    // `pointerdown`, not `click`: it fires before focus moves, so the menu is
    // already closing when the click lands on whatever is underneath.
    function onPointerDown(event: PointerEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    function onFocusIn(event: FocusEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('focusin', onFocusIn)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('focusin', onFocusIn)
    }
  }, [open])

  const label = me?.full_name || me?.email || 'Account'
  const initials = (me?.full_name || me?.email || '?')
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

  const item = {
    display: 'block',
    width: '100%',
    padding: '0.5rem 0.75rem',
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-secondary)',
    textAlign: 'left' as const,
    textDecoration: 'none',
    font: 'inherit',
    cursor: 'pointer',
  }

  return (
    <div ref={container} className="relative text-sm" style={{ marginTop: 'auto' }}>
      {open && (
        <div
          role="menu"
          aria-label={`Account: ${label}`}
          // Out of flow entirely: `bottom: 100%` sits it above the trigger and
          // `position: absolute` means opening it moves nothing.
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            marginBottom: '0.375rem',
            minWidth: collapsed ? '12rem' : undefined,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md, 0 8px 24px rgba(0,0,0,0.12))',
            padding: '0.25rem',
            zIndex: 'var(--z-dropdown, 300)' as unknown as number,
          }}
        >
          {collapsed && (
            <p
              className="m-0 truncate px-3 py-1 text-xs"
              style={{ color: 'var(--text-label)' }}
              title={me?.email ?? undefined}
            >
              {label}
            </p>
          )}
          <NavLink role="menuitem" to="/account" onClick={() => setOpen(false)} style={item}>
            Account & Password
          </NavLink>

          {/**
            * **Appearance — three options, not a switch.**
            *
            * `DESIGN-SYSTEM.md` §4 specifies three states and `lib/theme.ts`
            * implemented all three, but nothing in the app ever called
            * `setTheme` — so until 9 Sep dark mode could only be reached by
            * changing the OS setting. A three-state rule with no control is
            * one state plus whatever the OS says.
            *
            * A binary toggle cannot express it: `system` is the **default**, and
            * a switch showing "Dark: off" would be claiming an explicit light
            * choice nobody made. So it is a radio group — `menuitemradio` with
            * `aria-checked`, which is the correct role for a mutually exclusive
            * choice inside a menu.
            *
            * In the menu rather than the header because it is a per-viewer
            * preference, stored in `localStorage` and never sent to the server.
            */}
          <div
            role="group"
            aria-label="Appearance"
            style={{ borderTop: '1px solid var(--border)', marginTop: '0.25rem', paddingTop: '0.25rem' }}
          >
            <p
              className="m-0 px-3 pt-1 pb-0.5 text-xs"
              style={{ color: 'var(--text-label)' }}
            >
              Appearance
            </p>
            {THEMES.map((option) => {
              const selected = theme === option
              return (
                <button
                  key={option}
                  role="menuitemradio"
                  aria-checked={selected}
                  type="button"
                  // The menu stays open: picking a theme is a thing you may
                  // want to try twice, and closing it would make comparing the
                  // two a four-click job.
                  onClick={() => setTheme(option)}
                  style={{
                    ...item,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: selected ? 'var(--brand-ink)' : (item.color as string),
                    fontWeight: selected ? 600 : undefined,
                  }}
                >
                  {/* A check, not colour alone — the selected row is also the
                      only one carrying brand ink, and one affordance is not
                      enough for a choice this small. */}
                  <span aria-hidden="true" style={{ width: '0.75rem', flex: '0 0 auto' }}>
                    {selected ? '✓' : ''}
                  </span>
                  {THEME_LABELS[option]}
                </button>
              )
            })}
          </div>
          <button
            role="menuitem"
            type="button"
            onClick={() => {
              setOpen(false)
              onSignOut()
            }}
            style={item}
          >
            Sign out
          </button>
        </div>
      )}

      <button
        ref={trigger}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 text-left text-xs"
        style={{
          padding: collapsed ? '0.5rem' : '0.5rem 0.625rem',
          justifyContent: collapsed ? 'center' : undefined,
          background: open ? 'var(--sidebar-item-hover)' : 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
        }}
        title={collapsed ? label : undefined}
      >
        <span
          aria-hidden="true"
          className="flex shrink-0 items-center justify-center text-[0.625rem] font-bold"
          style={{
            width: '1.75rem',
            height: '1.75rem',
            borderRadius: '999px',
            background: 'var(--brand-tint)',
            color: 'var(--brand-ink)',
          }}
        >
          {initials}
        </span>
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1">
              <span
                className="block truncate font-medium"
                style={{ color: 'var(--text-primary)' }}
                title={me?.email ?? undefined}
              >
                {label}
              </span>
              <span className="block">{me?.role === 'admin' ? 'Administrator' : 'User'}</span>
            </span>
            {/* The chevron reflects state, so the trigger reads as a disclosure
                rather than as a label that happens to be clickable. */}
            <svg
              aria-hidden="true"
              width="10"
              height="10"
              viewBox="0 0 10 10"
              className="shrink-0"
              style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }}
            >
              <path d="M1 6.5 5 2.5l4 4" fill="none" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </>
        )}
      </button>
    </div>
  )
}
